/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext,
    IRequestContext
} from '@msdyn365-commerce/core';
import { Cart } from '@msdyn365-commerce/retail-proxy';

import { ICartState } from '../state-interfaces';
import { getCartState } from './get-cart-state';

/**
 * Input class for getCart data action.
 */
export class GetCartStateDataInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    private readonly catalogId?: number;

    constructor(apiSettings: ICommerceApiSettings, requestContext?: IRequestContext) {
        this.apiSettings = apiSettings;
        if (requestContext) {
            this.catalogId = getCatalogId(requestContext);
        }
    }

    public getCacheKey = () => `GetCart-chanId:${this.apiSettings.channelId}-catId:${this.catalogId ?? 0}`;

    public getCacheObjectType = () => 'Cart';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new GetCartStateDataInput(inputData.requestContext.apiSettings, inputData.requestContext);
};

/**
 * Data action to copy the cart for  actions.
 * @param input
 * @param ctx
 */
export async function getCartStateData(input: GetCartStateDataInput, ctx: IActionContext): Promise<ICartState | undefined> {
    const cartState = await getCartState(ctx);
    if (cartState && cartState.cart.Id !== undefined) {
        return cartState;
    }

    return undefined;
}

export default createObservableDataAction({
    id: '@msdyn365-commerce/global-state/get-cart-state-data',
    action: <IAction<Cart | undefined>>getCartStateData,
    input: createInput
});
